import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader2, Gift, Trash2, ShieldAlert, RefreshCw, UserMinus, UserCheck, Send } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantTier, setGrantTier] = useState("pro");
  const [grantNote, setGrantNote] = useState("");
  const [granting, setGranting] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");

  const isSuperadmin = user?.is_superadmin || user?.email === process.env.REACT_APP_ADMIN_EMAIL;

  const reload = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/comps/pending"),
      ]);
      setUsers(u.data || []);
      setPending(p.data || []);
    } catch (e) {
      toast.error("No tienes permiso para ver este panel o ocurrió un error.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const grantComp = async (e) => {
    e.preventDefault();
    if (!grantEmail.trim()) return;
    setGranting(true);
    try {
      const r = await api.post("/admin/comps/pending", {
        email: grantEmail.trim().toLowerCase(),
        tier: grantTier,
        note: grantNote,
      });
      if (r.data?.applied) {
        toast.success(`Membresía ${grantTier.toUpperCase()} aplicada a ${grantEmail} ✓`);
      } else {
        toast.success(`Comp pendiente guardado. Se aplicará cuando ${grantEmail} se registre.`);
      }
      setGrantEmail(""); setGrantNote("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo otorgar la membresía");
    } finally {
      setGranting(false);
    }
  };

  const revokeComp = async (userId, email) => {
    if (!confirm(`¿Revocar la membresía comp de ${email}?`)) return;
    try {
      await api.post(`/admin/users/${userId}/comp`, { is_comped: false });
      toast.success("Membresía revocada");
      reload();
    } catch (e) {
      toast.error("No se pudo revocar");
    }
  };

  const cancelPending = async (email) => {
    if (!confirm(`¿Cancelar el comp pendiente para ${email}?`)) return;
    try {
      await api.delete(`/admin/comps/pending/${encodeURIComponent(email)}`);
      toast.success("Pendiente cancelado");
      reload();
    } catch (e) {
      toast.error("No se pudo cancelar");
    }
  };

  const toggleSuperadmin = async (userId, currentVal, email) => {
    const action = currentVal ? "QUITAR" : "DAR";
    if (!confirm(`¿${action} permisos de superadmin a ${email}?`)) return;
    try {
      await api.post(`/admin/users/${userId}/superadmin`, { is_superadmin: !currentVal });
      toast.success(`Permisos actualizados`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const wipeAll = async () => {
    if (wipeConfirm !== "WIPE-ALL") {
      toast.error("Escribe exactamente WIPE-ALL para confirmar");
      return;
    }
    if (!confirm("⚠️ ÚLTIMA CONFIRMACIÓN: Esto borrará TODOS los usuarios, transacciones, deudas, tarjetas, y cancelará TODAS las suscripciones activas en Stripe. Solo tu cuenta superadmin sobrevivirá. ¿Continuar?")) {
      return;
    }
    setWiping(true);
    try {
      const r = await api.post("/admin/wipe-all", { confirm: "WIPE-ALL" });
      toast.success(`Borrado completado · ${r.data.stripe_subscriptions_cancelled} subs canceladas · ${r.data.wiped?.users || 0} usuarios eliminados`);
      setWipeConfirm("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al borrar");
    } finally {
      setWiping(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-7 h-7 text-violet-400 animate-spin" /></div>;
  }

  if (!isSuperadmin && users.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <ShieldAlert className="w-14 h-14 mx-auto text-rose-400 mb-3" />
        <h1 className="text-2xl font-bold">Acceso restringido</h1>
        <p className="mt-2 text-slate-400 text-sm">Este panel es solo para superadministradores.</p>
        <Link to="/" className="mt-6 inline-block text-violet-300 hover:text-violet-200">← Volver al panel</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-5 sm:p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-violet-300 font-bold">SUPERADMIN</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold">Panel de administración</h1>
          <p className="text-sm text-slate-400 mt-1">Otorga membresías comp, revoca accesos y gestiona los datos del sistema.</p>
        </div>
        <button onClick={reload} className="text-slate-400 hover:text-white p-2" data-testid="admin-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </header>

      {/* GRANT COMP FORM */}
      <section className="rounded-2xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 border border-violet-500/25 p-6" data-testid="admin-grant-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center"><Gift className="w-5 h-5 text-violet-300" /></div>
          <div>
            <h2 className="text-lg font-bold">Regalar membresía</h2>
            <p className="text-xs text-slate-400">Otorga acceso comp (sin pasar por Stripe). Si el usuario aún no existe, se aplica al registrarse.</p>
          </div>
        </div>
        <form onSubmit={grantComp} className="grid sm:grid-cols-[1fr_auto_auto] gap-3">
          <input
            type="email" placeholder="email@usuario.com"
            value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} required
            data-testid="admin-grant-email"
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-violet-500/60"
          />
          <select
            value={grantTier} onChange={(e) => setGrantTier(e.target.value)}
            data-testid="admin-grant-tier"
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none cursor-pointer"
          >
            <option value="pro" className="bg-[#15141b]">Pro</option>
            <option value="family" className="bg-[#15141b]">Familia</option>
          </select>
          <button
            type="submit" disabled={granting}
            data-testid="admin-grant-submit"
            className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white font-semibold px-5 py-3 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Otorgar
          </button>
        </form>
        <input
          type="text" placeholder="Nota interna (opcional, ej. 'Cliente VIP', 'Influencer')"
          value={grantNote} onChange={(e) => setGrantNote(e.target.value)}
          data-testid="admin-grant-note"
          className="mt-3 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-violet-500/60"
        />
      </section>

      {/* PENDING COMPS */}
      {pending.length > 0 && (
        <section className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid="admin-pending-section">
          <div className="px-5 py-4 border-b border-white/5">
            <h2 className="text-base font-bold">Comps pendientes <span className="text-xs font-normal text-slate-400 ml-2">({pending.length})</span></h2>
            <p className="text-[11px] text-slate-500">Estos usuarios todavía no se han registrado.</p>
          </div>
          <div className="divide-y divide-white/5">
            {pending.map((p, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`admin-pending-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-slate-200 truncate">{p.email}</div>
                  <div className="text-[11px] text-slate-500">Tier: <span className="text-violet-300 font-semibold">{p.tier?.toUpperCase()}</span>{p.note ? ` · ${p.note}` : ""}</div>
                </div>
                <button onClick={() => cancelPending(p.email)} className="text-slate-500 hover:text-rose-400 p-2" title="Cancelar pendiente" data-testid={`admin-cancel-pending-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* USERS LIST */}
      <section className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid="admin-users-section">
        <div className="px-5 py-4 border-b border-white/5">
          <h2 className="text-base font-bold">Usuarios registrados <span className="text-xs font-normal text-slate-400 ml-2">({users.length})</span></h2>
        </div>
        <div className="divide-y divide-white/5 max-h-[480px] overflow-y-auto">
          {users.map((u, i) => (
            <div key={u.id} className="px-5 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`admin-user-row-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-100 truncate">{u.email}</span>
                  {u.is_superadmin && <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded font-bold">SUPER</span>}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  <span className={`font-semibold ${u.effective_tier === "family" ? "text-fuchsia-300" : u.effective_tier === "pro" ? "text-violet-300" : "text-slate-500"}`}>
                    {u.effective_tier?.toUpperCase() || "FREE"}
                  </span>
                  {" · "}
                  <span>{u.effective_source}</span>
                  {u.comp_note && <span className="ml-2 italic">&ldquo;{u.comp_note}&rdquo;</span>}
                  {u.country_code && <span className="ml-2">· {u.country_code}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {u.is_comped && (
                  <button onClick={() => revokeComp(u.id, u.email)} className="text-[11px] text-rose-300 hover:text-rose-200 px-2 py-1 rounded hover:bg-rose-500/10" data-testid={`admin-revoke-${i}`}>
                    <UserMinus className="w-3.5 h-3.5 inline mr-1" /> Revocar
                  </button>
                )}
                {!u.is_comped && u.effective_tier === "free" && (
                  <button onClick={async () => {
                    try {
                      await api.post(`/admin/users/${u.id}/comp`, { is_comped: true, tier: "pro" });
                      toast.success("Pro comp otorgado"); reload();
                    } catch (e) { toast.error("Error"); }
                  }} className="text-[11px] text-emerald-300 hover:text-emerald-200 px-2 py-1 rounded hover:bg-emerald-500/10" data-testid={`admin-grant-quick-${i}`}>
                    <UserCheck className="w-3.5 h-3.5 inline mr-1" /> + Pro
                  </button>
                )}
                <button onClick={() => toggleSuperadmin(u.id, u.is_superadmin, u.email)} className="text-[11px] text-amber-300 hover:text-amber-200 px-2 py-1 rounded hover:bg-amber-500/10" data-testid={`admin-toggle-super-${i}`}>
                  {u.is_superadmin ? "− Super" : "+ Super"}
                </button>
              </div>
            </div>
          ))}
          {users.length === 0 && <div className="text-center text-slate-500 py-10 text-sm">No hay usuarios</div>}
        </div>
      </section>

      {/* DANGER ZONE — WIPE ALL */}
      <section className="rounded-2xl bg-rose-500/5 border border-rose-500/30 p-6" data-testid="admin-danger-section">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-rose-300" /></div>
          <div>
            <h2 className="text-lg font-bold text-rose-200">Zona peligrosa · Borrado total</h2>
            <p className="text-xs text-slate-400">Cancela TODAS las suscripciones activas en Stripe y borra TODOS los datos (usuarios, transacciones, deudas, tarjetas). Solo tu cuenta superadmin sobrevive.</p>
          </div>
        </div>
        <div className="bg-black/30 rounded-xl p-4 text-[12px] text-slate-300 leading-relaxed mb-4">
          Esto es <strong className="text-rose-200">irreversible</strong>. Úsalo para hacer un fresh-start antes del lanzamiento real. Una vez ejecutado:
          <ul className="mt-2 list-disc pl-5 space-y-0.5 text-slate-400">
            <li>Todas las suscripciones activas en Stripe se cancelan inmediatamente.</li>
            <li>Todos los usuarios (excepto tú) son eliminados de la DB.</li>
            <li>Cuentas, transacciones, deudas, tarjetas, presupuestos, recurrentes: borradas.</li>
            <li>Tus comps pendientes también se borran.</li>
          </ul>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3">
          <input
            type="text" placeholder={'Escribe "WIPE-ALL" para confirmar'}
            value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)}
            data-testid="admin-wipe-confirm"
            className="bg-black/40 border border-rose-500/30 rounded-xl px-4 py-3 text-sm outline-none focus:border-rose-500/60 font-mono"
          />
          <button
            onClick={wipeAll} disabled={wiping || wipeConfirm !== "WIPE-ALL"}
            data-testid="admin-wipe-btn"
            className="bg-rose-600 hover:bg-rose-500 text-white font-semibold px-5 py-3 rounded-xl text-sm flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {wiping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Borrar todo
          </button>
        </div>
      </section>
    </div>
  );
}
